import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { DayWisePlanDataTable } from '..';
import { optionalColumnsWithShift } from '../../staticData.js';
import {
  filterRows,
  getDatesForRange,
  getMonthNameWithYear,
  getoptionalColumnsByBU,
} from '../../utils/utils';
import styles from './DayWisePlan.module.css';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
} from '../../constants';
import ToolBar from '../ToolBar/ToolBar';
import { API } from '../../apis/api';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { withAllowedOperationsProvider } from '../../hocs';
import { buildErrorMessage } from '../../apis/calls';
import PlanUploadErrorsPopup from '../PlanUploadErrorsPopup/PlanUploadErrorsPopup';
import { formatDataToIncludeColour } from '../../utils/tableUtils';

const DayWisePlan = ({ hasActiveEdits, setHasActiveEdits }) => {
  const { showError, showPopup, showLoading, closePopUp } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const [planData, setPlanData] = useState({
    data: [],
    filteredData: [],
    startDate: '2020-05-26',
    endDate: '2020-06-20',
    lastUpdatedDate: null,
  });

  const [selectedOptionalCols, setSelectedOptionalCols] = useState([]);
  const [editActiveRowIndex, setEditActiveRowIndex] = useState(null);
  const [lastAsnGeneratedDate, setLastAsnGeneratedDate] = useState(
    moment('2020-03-03'),
  );
  const [activeMonthNameWithYear, setActiveMonthNameWithYear] = useState(
    getMonthNameWithYear(moment()),
  );
  const [callingAverage, setCallingAverage] = useState(null);

  const [isPlanErrorsOpen, setIsPlanErrorsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState();

  const populateEmptyPlanValues = (data, startDate, endDate) => {
    const dates = getDatesForRange(startDate, endDate);
    const dateStrings = dates.map(date => date.format(DATE_FORMAT.ISO));
    const result = [...data];
    result.forEach(vc => {
      for (const dateString of dateStrings) {
        if (!vc.plan[dateString]) {
          vc.plan[dateString] = {
            edited: false,
            quantity: 0,
          };
        }
      }
    });
    return result;
  };

  const processDataForTables = (planData, startDate, endDate, partialVcs) => {
    const vcsOfCurrentPlant = new Set(planData.map(data => data.vc));
    const mapVcInPlanToPartialVcData = partialVcs
      .filter(({ vc }) => vcsOfCurrentPlant.has(vc))
      .reduce(
        (acc, curr) => ({
          ...acc,
          [curr.vc]: curr,
        }),
        {},
      );

    const dataWithPartialDataCheck = planData.map(datum => {
      if (!mapVcInPlanToPartialVcData[datum.vc]) return datum;
      return {
        ...datum,
        partialData: mapVcInPlanToPartialVcData[datum.vc],
      };
    });

    return populateEmptyPlanValues(
      dataWithPartialDataCheck,
      startDate,
      endDate,
    );
  };

  const getPlanDataOfPlantAndLineForDates = async (
    plantValue,
    lineValue,
    startDate,
    endDate,
  ) => {
    if (!plantValue || !lineValue) return;
    const response = await API.get(API_RESOURCE_URLS.VC_QUANTITY, {
      params: {
        from: startDate,
        to: endDate,
        line: lineValue,
        plant: plantValue,
      },
    });
    return response.data;
  };

  const getPartialsVcsDataForPlantFilteredByLine = async (
    plantValue,
    lineName,
  ) => {
    const response = await API.get(API_RESOURCE_URLS.vcs(plantValue), {
      params: { partialData: true },
    });

    return response.data.filter(vcData =>
      vcData.lineAttributes
        .map(lineAttribute => lineAttribute.lineName)
        .includes(lineName),
    );
  };

  const loadPlanFromDates = async (startDate, endDate) => {
    // const closeLoadingSpinner = showLoading();
    if (!plant.value || !line.value) return;
    try {
      const planData = await getPlanDataOfPlantAndLineForDates(
        plant.value,
        line.value,
        startDate,
        endDate,
      );
      const data = formatDataToIncludeColour(planData);
      const partialsVcsOfPlantAndLine = await getPartialsVcsDataForPlantFilteredByLine(
        plant.value,
        line.label,
      );

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      ) {
        // closeLoadingSpinner();
        return;
      }
      const processedData = processDataForTables(
        data,
        startDate,
        endDate,
        partialsVcsOfPlantAndLine,
      );
      setPlanData({
        data: processedData,
        startDate,
        endDate,
        filteredData: processedData,
        lastUpdatedDate: moment(),
      });
      // closeLoadingSpinner();
    } catch (error) {
      console.error('Failed to load Plan.' + buildErrorMessage(error));
      showError({ contextText: 'Failed to load Plan', error });
    }
  };

  const loadPlanDataOfActiveMonth = () => {
    const selectedMonthNameWithYear = activeMonthNameWithYear.split('-'); // activeMonthNameWithYear will be in form Jan-2021

    const selectedMonthName = selectedMonthNameWithYear[0];
    const yearOfSelectedMonth = selectedMonthNameWithYear[1];

    const startDate = moment()
      .month(selectedMonthName)
      .year(yearOfSelectedMonth)
      .startOf('month');
    const startDateString = startDate.format(DATE_FORMAT.ISO);
    const endDate = moment()
      .month(selectedMonthName)
      .year(yearOfSelectedMonth)
      .endOf('month');
    const endDateString = endDate.format(DATE_FORMAT.ISO);
    return loadPlanFromDates(startDateString, endDateString);
  };

  const handleFileUpload = async file => {
    showLoading();
    const formData = new FormData();
    formData.append('file', file);

    try {
      showLoading();
      const response = await API.post(API_RESOURCE_URLS.VC_QUANTITY, formData);
      response.fileName = file.name;
      closePopUp();
      showPopup({
        response,
        info: 'File Name:' + file.name,
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.PLAN_UPLOAD_SUCCESS,
      }); //TODO:Check behaviour

      await loadPlanDataOfActiveMonth(); // refresh data
    } catch (error) {
      if (error.response?.status === 400) {
        setIsPlanErrorsOpen(true);
        setUploadErrors(error.response.data || []);
      } else if (error.response?.status === 403) {
        setIsPlanErrorsOpen(true);
        setUploadErrors([{ error: DISPLAY_MESSAGES.PLAN_UPLOAD_FORBIDDEN }]);
      } else {
        showPopup({
          error: error,
          type: MESSAGE_TYPE.FAILURE,
          contextText: DISPLAY_MESSAGES.PLAN_UPLOAD_ERROR,
        });
      }
    }
    closePopUp();
  };

  const filterPlanData = filters => {
    const rows = filterRows(planData.data, filters);
    setPlanData(prevState => ({ ...prevState, filteredData: [...rows] }));
  };

  const loadLastAsnGeneratedDate = async () => {
    try {
      if (!plant.value || !line.value) return;
      const response = await API.get(
        API_RESOURCE_URLS.LAST_ASN_GENERATED_DATE,
        {
          params: {
            plant: plant.value,
            line: line.value,
          },
        },
      );
      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return;
      const data = response.data;
      const lineData = data.filter(entry => entry.line === line.value)[0];
      setLastAsnGeneratedDate(moment(lineData.lastDate, DATE_FORMAT.ISO));
    } catch (error) {
      console.warn(
        'failed to get last-asn-generated-date ' + buildErrorMessage(error),
      );
      setLastAsnGeneratedDate(null);
    }
  };

  const calculateCallingAverageForMonth = (
    targetBalance,
    selectedMonthNameWithYear,
    filteredData,
  ) => {
    const currentMonthStringWithYear = getMonthNameWithYear(moment());
    const previousMonthNameWithYear = getMonthNameWithYear(
      moment().subtract(1, 'month'),
    );

    if (selectedMonthNameWithYear === previousMonthNameWithYear) return null;
    if (targetBalance === 0) return 0;

    const daysWithPlan = filteredData.reduce((acc, data) => {
      const planDaysWithQuantity = Object.keys(data.plan).filter(
        dateString => data.plan[dateString].quantity,
      );
      acc.push(...planDaysWithQuantity);
      return acc;
    }, []);

    const daysWithPlanSet = new Set(daysWithPlan);

    const dayNumbersWithPlan = [...daysWithPlanSet].map(dateString => {
      const date = moment(dateString, DATE_FORMAT.ISO);
      const day = Number(date.format('DD'));
      return day;
    });

    let daytoStartCountFrom =
      selectedMonthNameWithYear === currentMonthStringWithYear
        ? Number(moment().format('DD'))
        : 0;

    const noOfRemainingDaysWithPlan = dayNumbersWithPlan.filter(
      day => day >= daytoStartCountFrom,
    ).length;

    if (noOfRemainingDaysWithPlan === 0) return null;

    return Math.ceil(targetBalance / noOfRemainingDaysWithPlan);
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    loadPlanDataOfActiveMonth();
    loadLastAsnGeneratedDate();
    setEditActiveRowIndex(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value, activeMonthNameWithYear]);

  useEffect(() => {
    const monthBalance = planData.filteredData.reduce(
      (acc, vc) => acc + vc.balance,
      0,
    );

    setCallingAverage(
      calculateCallingAverageForMonth(
        monthBalance,
        activeMonthNameWithYear,
        planData.filteredData,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planData.filteredData]);

  useEffect(() => {
    if (editActiveRowIndex !== null) setHasActiveEdits(true);
    else setHasActiveEdits(false);
  }, [editActiveRowIndex, setHasActiveEdits]);

  return (
    <>
      <ToolBar
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
        optionalColumns={getoptionalColumnsByBU()}
        applyColumnsSelection={columnNames =>
          setSelectedOptionalCols([...columnNames])
        }
        selectedOptionalCols={selectedOptionalCols}
        lastUpdatedDate={planData.lastUpdatedDate}
        handleFileUpload={handleFileUpload}
        applyFilters={filters => {
          filterPlanData(filters);
        }}
        planData={planData}
        line={line}
        handleMonthSelect={setActiveMonthNameWithYear}
        callingAverage={callingAverage}
      />

      <div className={styles.container}>
        <div className={styles.tableView}>
          <DayWisePlanDataTable
            planData={planData}
            optionalColumns={optionalColumnsWithShift}
            selectedOptionalCols={selectedOptionalCols}
            editActiveRowIndex={editActiveRowIndex}
            startEditing={setEditActiveRowIndex}
            stopEditing={() => setEditActiveRowIndex(null)}
            lastAsnGeneratedDate={lastAsnGeneratedDate}
            updateRows={loadPlanDataOfActiveMonth}
            plant={plant.value}
            plantName={plant.label}
            line={line.value}
            lineName={line.label}
          />
        </div>
      </div>
      <PlanUploadErrorsPopup
        open={isPlanErrorsOpen}
        handleClose={() => setIsPlanErrorsOpen(false)}
        errors={uploadErrors}
      />
    </>
  );
};

export default withAllowedOperationsProvider(
  DayWisePlan,
  false,
  RESOURCE_TYPE.CHASSIS,
);
